import type {Texture} from 'three'
import {Group, Mesh, Vector2, Color, PlaneGeometry, ShaderMaterial} from 'three'
import type Common from './common'

import vertexShader from './shaders/backgroundVert'
import fragmentShader from './shaders/backgroundFrag'
import utilGLSL from './shaders/utilGlsl'

import Stars from './stars'
import type AssetsCta from './assets'
import type AssetsHero from './assetsHero'

export default class Background {
  group: Group = new Group()
  mesh: Mesh = new Mesh()
  stars: Stars = new Stars()
  starOffsetScaleY: number = 0.08
  isHero: boolean = true
  common?: Common
  assets?: AssetsCta | AssetsHero
  bgColor: Color = new Color(0x000000)
  centerColor: Color = new Color(0x0e178c)
  uniforms: {[uniform: string]: {value: number | Vector2 | Texture | Color}} = {
    uBorderRadius: {
      value: 0.0,
    },
    uDimensions: {
      value: new Vector2(0, 0),
    },
    uDiffuse: {
      value: this.stars.fbo.texture,
    },
    uCenterColor: {
      value: this.centerColor,
    },
    uColorProgress: {
      value: 0,
    },
    uBgColor: {
      value: this.bgColor,
    },
  }

  constructor(common: Common, assets: AssetsCta | AssetsHero, isHero: boolean = true) {
    this.isHero = isHero
    this.common = common
    this.assets = assets

    if (this.isHero) {
      this.starOffsetScaleY = 0.25
      this.bgColor.set(0x0d1117)
      this.centerColor.set(0x1d1594)
    }

    this.common.scene.add(this.group)
  }

  init() {
    this.stars.init(this.common, this.assets, this.starOffsetScaleY, this.isHero, this.bgColor)

    this.mesh = new Mesh(
      new PlaneGeometry(1, 1),
      new ShaderMaterial({
        vertexShader,
        fragmentShader: utilGLSL + fragmentShader,
        uniforms: this.uniforms,
        defines: {
          TYPE: this.isHero ? 0 : 1,
        },
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    )

    this.mesh.renderOrder = -1

    this.group.add(this.mesh)
  }

  resize(scale: number) {
    if (!this.common?.canvasWrapper) return
    const borderRadius = parseFloat(getComputedStyle(this.common?.canvasWrapper).borderRadius)
    const w = this.common?.wrapperDimentions.x * scale
    const h = this.common?.wrapperDimentions.y * scale

    if (this.uniforms.uDimensions) {
      if (this.uniforms.uDimensions.value instanceof Vector2) {
        this.uniforms.uDimensions.value.set(w, h)
      }
    }

    if (this.uniforms.uBorderRadius) {
      this.uniforms.uBorderRadius.value = borderRadius * scale
    }

    this.group.scale.set(w, h, 1)

    this.group.position.y = -(this.common?.sizes.y - this.common?.wrapperDimentions.y) * 0.5 * scale
    this.stars.resize()
  }

  speedUp() {
    this.stars.speedUp()
  }

  speedDown() {
    this.stars.speedDown()
  }

  update() {
    this.stars.update()
    if (this.uniforms.uColorProgress) {
      this.uniforms.uColorProgress.value = this.stars.speedProgress.current
    }
  }
}
